import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertCircle, ArrowLeft, Phone, Mail, MapPin, RefreshCw } from 'lucide-react';
import { useTechAuth } from '@/contexts/TechAuthContext';
import {
  createTechJobEvent,
  getTechJobDetail,
  getTechSchedule,
  type TechJobDetailResponse,
  type TechJobEventType,
  type TechScheduleRow,
} from '@/lib/tech-api-client';

function getErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return String(error || 'Unknown error');
}

function formatTimeEastern(iso: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;

  return d.toLocaleTimeString('en-US', {
    timeZone: 'America/New_York',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function getTodayYmdEastern(): string {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/New_York',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(new Date());

  const year = parts.find((p) => p.type === 'year')?.value;
  const month = parts.find((p) => p.type === 'month')?.value;
  const day = parts.find((p) => p.type === 'day')?.value;

  if (!year || !month || !day) {
    return new Date().toISOString().slice(0, 10);
  }

  return `${year}-${month}-${day}`;
}

export default function TechJobPage() {
  const navigate = useNavigate();
  const params = useParams();
  const jobId = String(params.jobId || '').trim();

  const { accessToken } = useTechAuth();
  const [loading, setLoading] = useState(false);
  const [eventLoading, setEventLoading] = useState<TechJobEventType | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [scheduleRow, setScheduleRow] = useState<TechScheduleRow | null>(null);
  const [detail, setDetail] = useState<TechJobDetailResponse | null>(null);

  const date = useMemo(() => {
    const qs = new URLSearchParams(window.location.search);
    return qs.get('date') || getTodayYmdEastern();
  }, []);

  const load = async () => {
    if (!accessToken || !jobId) return;

    setLoading(true);
    setError(null);

    try {
      const [scheduleRes, jobRes] = await Promise.all([
        getTechSchedule(accessToken, { date }),
        getTechJobDetail(accessToken, jobId, { date }),
      ]);

      const found = (scheduleRes.rows || []).find((row) => row.job.id === jobId) || null;
      setScheduleRow(found);
      setDetail(jobRes);
    } catch (e) {
      setError(getErrorMessage(e));
      setScheduleRow(null);
      setDetail(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, jobId]);

  const job = scheduleRow?.job;
  const customerName = [scheduleRow?.customer?.first_name, scheduleRow?.customer?.last_name].filter(Boolean).join(' ');
  const scheduledTime = formatTimeEastern(scheduleRow?.timestamps?.scheduled_for ?? null);

  const address1 = scheduleRow?.location?.address1 || null;
  const address2 = scheduleRow?.location?.address2 || null;
  const cityState = [scheduleRow?.location?.city, scheduleRow?.location?.state].filter(Boolean).join(', ');
  const zip = scheduleRow?.location?.zipCode || null;
  const line2 = [cityState, zip].filter(Boolean).join(' ');

  const phone = scheduleRow?.contact?.phone || null;
  const email = scheduleRow?.contact?.email || null;

  const dispatch = async (type: TechJobEventType) => {
    if (!accessToken || !jobId) return;

    setEventLoading(type);
    setError(null);
    try {
      await createTechJobEvent(accessToken, jobId, type, { assignmentId: scheduleRow?.assignment_id, date });
      await load();
    } catch (e) {
      setError(getErrorMessage(e));
    } finally {
      setEventLoading(null);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <button
            type="button"
            onClick={() => navigate('/schedule')}
            className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to schedule
          </button>

          <h1 className="mt-2 text-2xl font-bold text-gray-900">
            {job?.number ? `Job #${job.number}` : jobId ? `Job ${jobId}` : 'Job'}
          </h1>
          <p className="mt-1 text-gray-600">
            {scheduledTime ? `Scheduled: ${scheduledTime}` : 'Scheduled time: —'}
            {job?.status ? ` • Status: ${job.status}` : ''}
          </p>
        </div>

        <button
          type="button"
          onClick={load}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
          disabled={loading}
        >
          <RefreshCw className={['w-4 h-4', loading ? 'animate-spin' : ''].join(' ')} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="p-4 bg-gradient-to-r from-red-50 to-pink-50 border border-red-200 rounded-2xl">
          <div className="flex items-center gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
            <p className="text-sm text-red-800 font-medium">{error}</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Customer</div>
          <div className="mt-2 text-xl font-semibold text-gray-900">{customerName || '—'}</div>

          <div className="mt-4 flex items-start gap-3">
            <MapPin className="w-5 h-5 text-gray-500 mt-0.5" />
            <div>
              <div className="text-sm text-gray-900">{address1 || '—'}</div>
              {address2 ? <div className="text-sm text-gray-700">{address2}</div> : null}
              {line2 ? <div className="text-sm text-gray-500">{line2}</div> : null}
            </div>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {phone ? (
              <a
                href={`tel:${encodeURIComponent(phone)}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
              >
                <Phone className="w-4 h-4" />
                Call
              </a>
            ) : null}

            {email ? (
              <a
                href={`mailto:${encodeURIComponent(email)}`}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white border border-gray-200 hover:bg-gray-50"
              >
                <Mail className="w-4 h-4" />
                Email
              </a>
            ) : null}
          </div>

          <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-2">
            <button
              type="button"
              onClick={() => dispatch('en_route')}
              disabled={eventLoading !== null}
              className="px-4 py-3 rounded-2xl bg-blue-600 text-white font-semibold hover:bg-blue-700 disabled:opacity-60"
            >
              {eventLoading === 'en_route' ? 'Sending…' : 'En Route'}
            </button>
            <button
              type="button"
              onClick={() => dispatch('on_site')}
              disabled={eventLoading !== null}
              className="px-4 py-3 rounded-2xl bg-emerald-600 text-white font-semibold hover:bg-emerald-700 disabled:opacity-60"
            >
              {eventLoading === 'on_site' ? 'Sending…' : 'On Site'}
            </button>
            <button
              type="button"
              onClick={() => dispatch('depart')}
              disabled={eventLoading !== null}
              className="px-4 py-3 rounded-2xl bg-gray-900 text-white font-semibold hover:bg-gray-800 disabled:opacity-60"
            >
              {eventLoading === 'depart' ? 'Sending…' : 'Depart'}
            </button>
          </div>

          <div className="mt-6 text-xs text-gray-500">
            These buttons update SuccessWare assignment timestamps (dispatched / on-site / completed).
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-2xl p-5">
          <div className="text-sm text-gray-500">Debug (temporary)</div>
          <div className="mt-2 text-xs text-gray-600 break-words">jobId: {jobId || '—'}</div>
          <div className="mt-1 text-xs text-gray-600 break-words">assignmentId: {scheduleRow?.assignment_id || '—'}</div>
          {detail?.warnings?.length ? (
            <div className="mt-2 text-xs text-amber-800">{detail.warnings.join(' ')}</div>
          ) : null}
          <details className="mt-3">
            <summary className="cursor-pointer text-xs text-gray-700">Job details payload</summary>
            <pre className="mt-2 text-[11px] text-gray-600 overflow-auto max-h-64 bg-gray-50 border border-gray-100 rounded-xl p-3">
              {JSON.stringify(detail?.job_details ?? null, null, 2)}
            </pre>
          </details>
        </div>
      </div>
    </div>
  );
}
